import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Card, CardContent, Typography, Button, Table, TableHead, TableRow,
  TableCell, TableBody, Chip, IconButton, Alert, Tooltip,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField, MenuItem,
  InputAdornment
} from '@mui/material';
import { Add, OpenInNew, Delete, Search, CalculateOutlined, Home, WarningAmber } from '@mui/icons-material';
import axios from 'axios';
import { fmt } from '../utils/currency';

const API  = process.env.REACT_APP_API_URL;
const hdrs = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

const STATUS_COLOR = {
  'Open': 'default', 'Calculated': 'info', 'Pending Approval': 'warning',
  'Approved': 'success', 'Posted': 'primary',
};


const emptyRun = { periodCode: '', payGroupCode: 'ALL', runDate: new Date().toISOString().split('T')[0] };

const PayrollRunList = () => {
  const navigate = useNavigate();
  const [runs,       setRuns]       = useState([]);
  const [periods,    setPeriods]    = useState([]);
  const [payGroups,  setPayGroups]  = useState([]);
  const [search,     setSearch]     = useState('');
  const [filterSt,   setFilterSt]   = useState('');
  const [error,      setError]      = useState('');
  const [success,    setSuccess]    = useState('');
  const [dlg,          setDlg]        = useState(false);
  const [form,         setForm]       = useState(emptyRun);
  const [saving,       setSaving]     = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const load = useCallback(async () => {
    try {
      const [rRes, pRes, gRes] = await Promise.all([
        axios.get(`${API}/api/payroll/runs`, { headers: hdrs() }),
        axios.get(`${API}/api/payroll/periods`, { headers: hdrs() }),
        axios.get(`${API}/api/employees/pay-groups`, { headers: hdrs() }),
      ]);
      setRuns(rRes.data || []);
      setPeriods((pRes.data || []).filter(p => p.status === 'Open'));
      setPayGroups(gRes.data || []);
    } catch { setError('Failed to load payroll runs'); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = runs.filter(r => {
    const match = !search || r.runNo.toLowerCase().includes(search.toLowerCase()) ||
      r.periodName.toLowerCase().includes(search.toLowerCase()) ||
      r.payGroupCode.toLowerCase().includes(search.toLowerCase());
    const st = !filterSt || r.status === filterSt;
    return match && st;
  });

  const createRun = async () => {
    if (!form.periodCode) { setError('Select a period'); return; }
    setSaving(true); setError('');
    try {
      const res = await axios.post(`${API}/api/payroll/runs`, form, { headers: hdrs() });
      setSuccess(`Run ${res.data.runNo} created`); setDlg(false); load();
      setTimeout(() => setSuccess(''), 3000);
      navigate(`/payroll/runs/${res.data.runNo}`);
    } catch (err) { setError(err.response?.data?.error || 'Create failed'); }
    finally { setSaving(false); }
  };

  const confirmDelete = async () => {
    try {
      await axios.delete(`${API}/api/payroll/runs/${deleteTarget.runNo}`, { headers: hdrs() });
      setDeleteTarget(null);
      setSuccess(`Run ${deleteTarget.runNo} deleted`); load();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) { setError(err.response?.data?.error || 'Delete failed'); }
  };

  return (
    <Box sx={{ maxWidth: 1100, mx: 'auto', mt: 4, mb: 6 }}>
      {/* Header */}
      <Card elevation={3} sx={{ mb: 2, background: 'linear-gradient(135deg, #263238 0%, #37474f 100%)', color: '#fff' }}>
        <CardContent sx={{ py: 2 }}>
          <Box display="flex" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={1}>
            <Box display="flex" alignItems="center" gap={1.5}>
              <Tooltip title="Payroll Home">
                <IconButton size="small" onClick={() => navigate('/payroll')}
                  sx={{ color: '#fff', bgcolor: 'rgba(255,255,255,0.15)', '&:hover': { bgcolor: 'rgba(255,255,255,0.25)' } }}>
                  <Home fontSize="small" />
                </IconButton>
              </Tooltip>
              <CalculateOutlined />
              <Typography variant="h6" fontWeight={700}>Payroll Runs</Typography>
              <Chip label={`${runs.length} total`} size="small"
                sx={{ bgcolor: 'rgba(255,255,255,0.15)', color: '#fff' }} />
            </Box>
            <Button startIcon={<Add />} variant="contained"
              onClick={() => { setForm(emptyRun); setError(''); setDlg(true); }}
              sx={{ bgcolor: 'rgba(255,255,255,0.15)', '&:hover': { bgcolor: 'rgba(255,255,255,0.25)' } }}>
              New Run
            </Button>
          </Box>
        </CardContent>
      </Card>

      {error   && <Alert severity="error"   sx={{ mb: 2 }}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

      {/* Filters */}
      <Card elevation={2} sx={{ mb: 2 }}>
        <CardContent sx={{ py: 1.5 }}>
          <Box display="flex" gap={2} flexWrap="wrap" alignItems="center">
            <TextField placeholder="Search run, period, group…" size="small" value={search}
              onChange={e => setSearch(e.target.value)} sx={{ minWidth: 240 }}
              InputProps={{ startAdornment: <InputAdornment position="start"><Search fontSize="small" /></InputAdornment> }} />
            <TextField select label="Status" size="small" value={filterSt}
              onChange={e => setFilterSt(e.target.value)} sx={{ minWidth: 160 }}
              InputLabelProps={{ shrink: true }}
              SelectProps={{ displayEmpty: true }}>
              <MenuItem value=""><em>All Statuses</em></MenuItem>
              {Object.keys(STATUS_COLOR).map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
            </TextField>
          </Box>
        </CardContent>
      </Card>

      <Card elevation={2}>
        <CardContent sx={{ p: 0 }}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: 'grey.100' }}>
                <TableCell><strong>Run No</strong></TableCell>
                <TableCell><strong>Period</strong></TableCell>
                <TableCell><strong>Pay Group</strong></TableCell>
                <TableCell align="right"><strong>Head Count</strong></TableCell>
                <TableCell align="right"><strong>Gross Pay</strong></TableCell>
                <TableCell align="right"><strong>Net Pay</strong></TableCell>
                <TableCell align="center"><strong>Status</strong></TableCell>
                <TableCell align="right"><strong>Actions</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filtered.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center">
                    <Typography variant="body2" color="text.secondary" py={3}>
                      {runs.length === 0
                        ? 'No payroll runs yet. Click "New Run" to start.'
                        : 'No runs match the filter.'}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : filtered.map(r => (
                <TableRow key={r.runNo} hover sx={{ cursor: 'pointer' }}
                  onClick={() => navigate(`/payroll/runs/${r.runNo}`)}>
                  <TableCell>
                    <Typography variant="caption" fontFamily="monospace" fontWeight={700}>{r.runNo}</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" fontWeight={600}>{r.periodName}</Typography>
                    <Typography variant="caption" fontFamily="monospace" color="text.secondary">{r.periodCode}</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{r.payGroupCode}</Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="body2">{r.headCount || '—'}</Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="body2">{fmt(r.totalGross)}</Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="body2" fontWeight={700}>{fmt(r.totalNetPay)}</Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Chip label={r.status} size="small" color={STATUS_COLOR[r.status] || 'default'} />
                  </TableCell>
                  <TableCell align="right" onClick={e => e.stopPropagation()}>
                    <Tooltip title="Open Run">
                      <IconButton size="small" color="primary"
                        onClick={() => navigate(`/payroll/runs/${r.runNo}`)}>
                        <OpenInNew fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    {['Open', 'Calculated'].includes(r.status) && (
                      <Tooltip title="Delete">
                        <IconButton size="small" color="error" onClick={() => setDeleteTarget(r)}>
                          <Delete fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Delete Confirmation Dialog */}
      <Dialog open={Boolean(deleteTarget)} onClose={() => setDeleteTarget(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'error.main' }}>
          <WarningAmber color="error" /> Delete Payroll Run
        </DialogTitle>
        <DialogContent dividers>
          <Typography variant="body1" gutterBottom>
            Are you sure you want to delete run <strong>{deleteTarget?.runNo}</strong>?
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Period: <strong>{deleteTarget?.periodName}</strong> &nbsp;·&nbsp;
            Group: <strong>{deleteTarget?.payGroupCode}</strong>
          </Typography>
          <Typography variant="body2" color="error" sx={{ mt: 1.5 }}>
            This will permanently remove the run and all its calculated entries.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)}>Cancel</Button>
          <Button variant="contained" color="error" startIcon={<Delete />} onClick={confirmDelete}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* New Run Dialog */}
      <Dialog open={dlg} onClose={() => setDlg(false)} maxWidth="xs" fullWidth>
        <DialogTitle>New Payroll Run</DialogTitle>
        <DialogContent dividers>
          {error && <Alert severity="error" sx={{ mb: 1.5 }}>{error}</Alert>}
          <Box display="flex" flexDirection="column" gap={2}>
            <TextField select label="Period *" size="small" fullWidth value={form.periodCode}
              onChange={e => setForm(f => ({ ...f, periodCode: e.target.value }))}
              InputLabelProps={{ shrink: true }}
              SelectProps={{ displayEmpty: true, MenuProps: { PaperProps: { sx: { maxHeight: 300 } } } }}
              helperText="Only Open periods are shown">
              <MenuItem value=""><em>— Select period —</em></MenuItem>
              {periods.map(p => <MenuItem key={p.periodCode} value={p.periodCode}>{p.periodName}</MenuItem>)}
            </TextField>
            <TextField select label="Pay Group *" size="small" fullWidth value={form.payGroupCode}
              onChange={e => setForm(f => ({ ...f, payGroupCode: e.target.value }))}
              InputLabelProps={{ shrink: true }}
              SelectProps={{ displayEmpty: true }}>
              <MenuItem value="ALL">ALL — All Active Employees</MenuItem>
              {payGroups.map(g => <MenuItem key={g.groupCode} value={g.groupCode}>{g.name}</MenuItem>)}
            </TextField>
            <TextField label="Run Date" type="date" size="small" fullWidth value={form.runDate}
              onChange={e => setForm(f => ({ ...f, runDate: e.target.value }))}
              InputLabelProps={{ shrink: true }} />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDlg(false)}>Cancel</Button>
          <Button variant="contained" onClick={createRun} disabled={saving}
            sx={{ bgcolor: '#263238', '&:hover': { bgcolor: '#37474f' } }}>
            {saving ? 'Creating…' : 'Create Run'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PayrollRunList;
